import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface FocusTrapOptions {
  /**
   * Whether the trap is active. Modals that mount/unmount on open can leave
   * this at the default `true`. Always-mounted surfaces toggled via an `open`
   * flag (e.g. RightPanel) should pass that flag here.
   */
  active?: boolean;
  /** Escape handler. Omit to leave Escape handling to the caller. */
  onClose?: () => void;
  /**
   * Element to focus when the trap activates. Falls back to the first
   * focusable descendant, then the container itself.
   */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /**
   * Move focus into the dialog on activate. Default `true`. Pass `false` when
   * the dialog already manages its own initial focus (e.g. an `autoFocus`
   * field) — restore-on-close still applies.
   */
  manageInitialFocus?: boolean;
  /** Lock body scroll while active. Default `false`. */
  lockScroll?: boolean;
}

/**
 * Accessible dialog focus management: traps Tab/Shift+Tab within the
 * container, restores focus to the trigger element on close, and optionally
 * closes on Escape. Covers WCAG 2.1.2 (no keyboard trap — escapable) and
 * 2.4.3 (focus order). Dependency-free; one source of truth for every modal.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  options: FocusTrapOptions = {},
) {
  const {
    active = true,
    onClose,
    initialFocusRef,
    manageInitialFocus = true,
    lockScroll = false,
  } = options;

  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  // Read the latest onClose without resubscribing when callers pass an inline arrow.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!active) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    // Defer initial focus so the target exists and has animated in.
    let focusTimer = 0;
    if (manageInitialFocus) {
      focusTimer = window.setTimeout(() => {
        const root = containerRef.current;
        if (!root) return;
        const target =
          initialFocusRef?.current ??
          root.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ??
          root;
        if (target === root && !root.hasAttribute('tabindex')) {
          root.setAttribute('tabindex', '-1');
        }
        target.focus();
      }, 50);
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onCloseRef.current) {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = containerRef.current;
      if (!root) return;
      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeEl = document.activeElement;
      if (e.shiftKey) {
        if (activeEl === first || !root.contains(activeEl)) {
          e.preventDefault();
          last.focus();
        }
      } else if (activeEl === last || !root.contains(activeEl)) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);

    let prevOverflow = '';
    if (lockScroll) {
      prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    return () => {
      if (focusTimer) window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', onKeyDown);
      if (lockScroll) document.body.style.overflow = prevOverflow;
      const previous = previouslyFocusedRef.current;
      previouslyFocusedRef.current = null;
      if (previous && typeof previous.focus === 'function' && previous.isConnected) {
        previous.focus();
      }
    };
  }, [active, containerRef, initialFocusRef, manageInitialFocus, lockScroll]);
}
